import clsx from 'clsx';
import type { PassViewRecord } from '../../../utils/fetchWeldViewApis';
import {
  deriveBinaryPassStatus,
  formatBugTypeDisplay,
  formatPassDurationDisplay,
  formatPassTimestampDisplay,
  formatStatusMethodLabel,
  passStatusBadgeClass,
} from '../../../utils/passViewFormatters';
import {
  violationsForPassName,
  type WpsViolationRecord,
} from '../../../utils/wpsViolationModel';
import { WpsViolationSummaryPanel } from './WpsViolationSummaryPanel';

interface PassDetailPanelProps {
  pass: PassViewRecord | null;
  violations?: WpsViolationRecord[];
  limitsConfigured?: boolean;
  statusCalculationMethod?: string;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[rgba(0,39,63,0.03)] px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-medium text-blueprint-blue">{value}</p>
    </div>
  );
}

export const PassDetailPanel = ({
  pass,
  violations = [],
  limitsConfigured = true,
  statusCalculationMethod = 'Instantaneous',
}: PassDetailPanelProps) => {
  if (!pass) {
    return (
      <div className="flex h-full min-h-[200px] flex-1 items-center justify-center rounded-xl border border-dashed border-[#E2E5EA] bg-white/60 p-6">
        <p className="text-center text-sm text-heritage-grey">
          Select a pass on the left to view status, timing, and WPS limit detail.
        </p>
      </div>
    );
  }

  const status = deriveBinaryPassStatus(pass);
  const passViolations = violationsForPassName(violations, pass.name || '');
  const checkedUsingLabel = formatStatusMethodLabel(statusCalculationMethod);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
      <div className="shrink-0 border-b border-[#E2E5EA] px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-[15px] font-semibold text-blueprint-blue">
            {pass.name?.trim() || 'Pass'}
          </h3>
          <span
            className={clsx(
              'inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
              passStatusBadgeClass(status),
            )}
          >
            {status}
          </span>
        </div>
        <p className="mt-1 text-xs text-heritage-grey">
          Checked using {checkedUsingLabel}
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Welder ID" value={pass.welderId || '—'} />
          <Field label="Torch" value={pass.torch?.trim() || '—'} />
          <Field label="CW / CCW" value={formatBugTypeDisplay(pass.bugType)} />
          <Field label="Station" value={pass.stationNumber?.trim() || '—'} />
          <Field label="Start" value={formatPassTimestampDisplay(pass.startTime)} />
          <Field label="End" value={formatPassTimestampDisplay(pass.endTime)} />
          <Field label="Duration" value={formatPassDurationDisplay(pass)} />
          <Field label="Job" value={pass.jobNumber || '—'} />
        </div>

        <WpsViolationSummaryPanel
          violations={passViolations}
          limitsConfigured={limitsConfigured}
          checkedUsingLabel={checkedUsingLabel}
          sectionTitle="Parameters out of limit"
          sectionSubtitle={
            passViolations.length > 0
              ? `${passViolations.length} location${passViolations.length === 1 ? '' : 's'} for this pass`
              : 'No limit violations for this pass'
          }
        />
      </div>
    </div>
  );
};
